import { useMemo, useState, useEffect, useRef } from "react";
import { Button } from "~/components/ui/button";
import {
  LayoutList,
  Map,
  Settings2,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { ClientOnly } from "~/components/blocks/ClientOnly/client-only";
import AutoPlaces from "./AutoPlaces";
import { parseAutocompleteSearchLocation } from "~/lib/helpers/autoCompleteSearchHelper";
import { useLocation, useNavigate, useRevalidator } from "@remix-run/react";
import { useCurrentCurrency } from "~/lib/helpers/CurrencyHelper";
import { Workspace } from "./interfaces";
import {
  formatAreaQueryCityParam,
  formatAreaQueryCountryParam,
  formatAreaQueryStateParam,
} from "~/lib/helpers/StringHelper";
import {
  isOfficeTypeSlug,
  navigateOrRevalidateLocationSearch,
} from "~/components/blocks/Geosearch/locationGeosearch";

export async function loader({ params }: { params: any }) {
  const googleMapKey = process.env.PUBLIC_REMIX_GOOGLE_MAP;
  // Replace with your API endpoint

  return { googleMapKey: googleMapKey };
}


export default function MobileSearchBar({
  toggleDrawer,
  setWidth,
  rightWidth,
  setDisplayMode,
  callGeoSearch,
  filterData,
  setFilterData,
  query = '',
  displayMode,
  workspace,
  setMapLoaded,
  areaSummary,
  setRecalibrate,
  onLocationChange,
}: {
  toggleDrawer: VoidFunction;
  setWidth: Function;
  rightWidth: number;
  setDisplayMode: Function;
  callGeoSearch: Function;
  filterData: any;
  setFilterData: Function;
  query: string;
  displayMode: string;
  workspace: Workspace;
  setMapLoaded?: Function;
  areaSummary?: any;
  setRecalibrate?: (v: boolean) => void;
  onLocationChange?: (params: Record<string, string>) => void;
}) {

  const navigate = useNavigate();
  const routeLocation = useLocation();
  const revalidator = useRevalidator();

  // --- Scroll State for Hiding Bottom Bar ---
  const [showBottomBar, setShowBottomBar] = useState(true);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      // 1. Clamp negative values caused by iOS rubber-band bounce
      const currentScrollY = Math.max(0, window.scrollY);
      const delta = currentScrollY - lastScrollY.current;

      // 2. Always show the bottom bar when at or near the top of the page
      if (currentScrollY <= 10) {
        setShowBottomBar(true);
        lastScrollY.current = currentScrollY;
        return;
      }

      // 3. Ignore tiny micro-scrolls (< 5px) to prevent layout jumping/jittering
      if (Math.abs(delta) < 5) return;

      if (delta > 0) {
        // Scrolling down → hide
        setShowBottomBar(false);
      } else {
        // Scrolling up → show
        setShowBottomBar(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLocationSelect = (target: string) => {
    if (!target) return;

    if (setRecalibrate) {
      setRecalibrate(true);
    }

    if (areaSummary) {
      const parsed = parseAutocompleteSearchLocation(target);
      if (parsed) {
        const payload: Record<string, string> = {
          fromSearchBar: "1",
          country: formatAreaQueryCountryParam(parsed.countryCode),
          scope: "",
        };
        if (parsed.state) {
          payload.state = formatAreaQueryStateParam(parsed.state);
        } else {
          payload.state = "reset";
        }
        if (parsed.city) {
          payload.city = formatAreaQueryCityParam(parsed.city);
        } else if (parsed.state) {
          payload.city = "reset";
        } else {
          payload.city = "reset";
        }
        if (parsed.latitude) {
          payload.latitude = parsed.latitude;
        } else {
          payload.latitude = "reset";
        }
        if (parsed.longitude) {
          payload.longitude = parsed.longitude;
        } else {
          payload.longitude = "reset";
        }
        if (parsed.text) {
          payload.text = parsed.text;
        } else {
          payload.text = "reset";
        }
        callGeoSearch(payload);
        onLocationChange?.(payload);
        return;
      }
    }

    navigateOrRevalidateLocationSearch({
      target,
      currentPathname: routeLocation.pathname,
      currentSearch: routeLocation.search,
      navigate: (to) => navigate(to),
      revalidate: () => revalidator.revalidate(),
    });
  };

  const { activeFilterCount, hasActiveFilters } = useMemo(() => {
    const urlParams = new URLSearchParams(routeLocation.search);

    // 1. Office Types
    const hasOfficeTypes =
      Array.isArray(filterData?.officeTypes) &&
      filterData.officeTypes.length > 0 &&
      !filterData.officeTypes.includes("All");
    const workspaceTypeParam = urlParams.get("workspaceType");
    const hasUrlWorkspaceType = Boolean(
      workspaceTypeParam && workspaceTypeParam !== "All"
    );
    const segments = routeLocation.pathname
      .split("?")[0]
      .split("/")
      .filter(Boolean);
    const lastSeg = segments[segments.length - 1];
    const hasPathOfficeType = isOfficeTypeSlug(lastSeg);
    const officeTypeActive =
      hasOfficeTypes || hasUrlWorkspaceType || hasPathOfficeType;

    // 2. Price
    const hasPrice =
      Array.isArray(filterData?.price) &&
      filterData.price.some(
        (value: any) =>
          value !== null && value !== undefined && value !== 0 && value !== ""
      );
    const hasUrlPrice = Boolean(
      urlParams.get("minPrice") ||
      urlParams.get("maxPrice") ||
      query?.includes("minPrice") ||
      query?.includes("maxPrice")
    );
    const priceActive = hasPrice || hasUrlPrice;

    // 3. Desk Range / People
    const hasDeskRange =
      Array.isArray(filterData?.deskRange) &&
      filterData.deskRange.some(
        (value: any) =>
          value !== null && value !== undefined && value !== 0 && value !== ""
      );
    const hasUrlDesk = Boolean(
      urlParams.get("minPeople") ||
      urlParams.get("maxPeople") ||
      query?.includes("minPeople") ||
      query?.includes("maxPeople")
    );
    const deskActive = hasDeskRange || hasUrlDesk;

    // 4. Promo / Sale
    const hasSale = Boolean(filterData?.promo || filterData?.sale);
    const hasUrlSale = Boolean(
      urlParams.get("promo") ||
      urlParams.get("sale") ||
      query?.includes("promo")
    );
    const saleActive = hasSale || hasUrlSale;

    // 5. Amenities / Facilities
    const hasAmenities =
      Array.isArray(filterData?.amenities) && filterData.amenities.length > 0;
    const hasFacilities =
      Array.isArray(filterData?.facilities) &&
      filterData.facilities.length > 0;
    const hasUrlAmenities = Boolean(
      urlParams.get("amenities") || urlParams.get("facilities")
    );
    const amenitiesActive =
      hasAmenities || hasFacilities || hasUrlAmenities;

    // 6. Workspace ID
    const hasWorkspace = Boolean(filterData?.workspaceId);
    const hasUrlWorkspace = Boolean(
      urlParams.get("workspaceId") || query?.includes("workspaceId")
    );
    const workspaceActive = hasWorkspace || hasUrlWorkspace;

    // Calculate total active filter categories
    const count = [
      officeTypeActive,
      priceActive,
      deskActive,
      saleActive,
      amenitiesActive,
      workspaceActive,
    ].filter(Boolean).length;

    return {
      activeFilterCount: count,
      hasActiveFilters: count > 0,
    };
  }, [filterData, query, routeLocation.search, routeLocation.pathname]);

  return (
    <div className="fixed w-full z-[40] flex flex-col">

      {/* Top Row: Full-width Location Input */}
      <div className="relative z-20 flex h-16 w-full items-center bg-white px-[14px] shadow-sm border-b border-gray-100">
        <div className="flex-1">
          <ClientOnly>
            {() => (
              <AutoPlaces
                mobile
                fixed
                onLocationSelect={handleLocationSelect}
                inputStyleClassName="w-full rounded-full pl-10 pr-10 bg-oh-light-aqua border border-oh-primary text-slate-800 focus-visible:ring-0 focus-visible:border-oh-primary"
                hideLocateFixed
              />
            )}
          </ClientOnly>
        </div>
      </div>

      {/* Bottom Row: Filter and Map Action Buttons */}
      <div
        className={`relative z-10 w-full overflow-hidden transition-[height] duration-300 ease-in-out ${showBottomBar ? "h-12" : "h-0"
          }`}
      >
        {/* Inner div maintains fixed height to avoid squishing content while shrinking */}
        <div className="flex h-12 w-full items-center justify-between bg-white border-b border-gray-200 shadow-md">

          {/* Filter Button */}
          <button
            type="button"
            onClick={toggleDrawer}
            className="flex flex-1 items-center justify-center gap-2 h-full border-r border-gray-200 text-base font-medium transition-colors focus:outline-none bg-white text-gray-800 hover:bg-gray-50"
            aria-label="Filter menu"
          >
            <Settings2 className={`h-4 w-4 ${hasActiveFilters ? "text-black" : "text-gray-800"}`} />
            <span className={`text-base ${hasActiveFilters ? "font-bold text-black" : "font-medium text-black"}`}>
              Filter {hasActiveFilters ? `(${activeFilterCount})` : ""}
            </span>
          </button>

          {/* Map Toggle Button */}
          <button
            type="button"
            className="flex flex-1 items-center justify-center gap-2 h-full text-base font-medium transition-colors focus:outline-none bg-white text-gray-800 hover:bg-gray-50"
            onClick={() => {
              if (setMapLoaded) {
                setMapLoaded(true);
              }
              if (rightWidth === 100) {
                setWidth(0);
              } else {
                setWidth(100);
              }
            }}
            aria-label={rightWidth === 100 ? "Close map" : "Map view"}
          >
            {rightWidth === 100 ? (
              <>
                <LayoutList className="h-4 w-4 text-black" aria-hidden="true" />
                <span className="text-base font-medium text-black">List</span>
              </>
            ) : (
              <>
                <Map className="h-4 w-4 text-gray-800" aria-hidden="true" />
                <span className="text-base font-medium text-black">Map</span>
              </>
            )}
          </button>
        </div>
      </div>

    </div >
  );
}
